import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, CheckCircle2, Copy, ImagePlus, Loader2, MessageSquarePlus, Pencil, RotateCcw, UserCheck, X } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '../../../components/ui/badge';
import { Button } from '../../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import BildLightbox from '../../../components/ui/bild-lightbox';
import { MentionText } from '../../../components/ui/mention-textarea';
import { MarkdownEditor } from '../../../components/ui/markdown-editor';
import { MarkdownText } from '../../../components/ui/markdown-text';
import { erwaehnteKonten } from '../../../lib/testing/erwaehnungen';
import { nummernImText, type TicketVerweis } from '../../../lib/testing/ticketverweise';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import { usePermission } from '../../../components/rbac/use-permission';
import { P } from '../../../lib/rbac/permissions.generated';
import {
  testingApi,
  SCREENSHOT_MAX_BYTES,
  SCREENSHOT_TYPES,
  TICKET_KIND_LABEL,
  TICKET_SEVERITY_LABEL,
  TICKET_STATUS_LABEL,
  type TicketAssigneeDto,
  type TicketAttachmentDto,
  type TicketDetailDto,
  type TicketKind,
  type TicketSeverity,
  type TicketStatus,
} from '../../../lib/api/testing';
import { abnahmeApi, type StoryAuswahlDto } from '../../../lib/api/abnahme';

const zeit = (iso: string | null): string => {
  if (!iso) return '—';
  const datum = new Date(iso);
  return Number.isNaN(datum.getTime()) ? '—' : datum.toLocaleString('de-DE');
};

type RequestZeile = { method?: string; path?: string; status?: number; requestId?: string };
type KonsolenZeile = { level?: string; message?: string };

type BenutzerKontext = {
  accountId?: string;
  rollen?: Array<{ rolle: string; geltung?: string; aktiv?: boolean; standorte?: number[] }>;
  rechte?: Record<string, string>;
};

/**
 * Wer gemeldet hat — Konto, Rollen und wirksame Rechte zum Zeitpunkt der Meldung
 * (T-0149). Die Rechte sind viele; sie stehen eingeklappt darunter.
 */
function BenutzerAngaben({ benutzer }: Readonly<{ benutzer: BenutzerKontext }>) {
  const rechte = Object.entries(benutzer.rechte ?? {});
  return (
    <div className="space-y-1">
      <div className="text-xs font-medium text-muted-foreground">Melder</div>
      {benutzer.accountId ? (
        <p className="break-all font-mono text-xs">Konto {benutzer.accountId}</p>
      ) : null}
      {(benutzer.rollen ?? []).map((rolle, index) => (
        <p key={index} className="font-mono text-xs">
          {rolle.rolle} · {rolle.geltung || '—'}
          {rolle.standorte && rolle.standorte.length > 0 ? ` · Standorte ${rolle.standorte.join(', ')}` : ''}
          {rolle.aktiv === false ? ' · inaktiv' : ''}
        </p>
      ))}
      {rechte.length > 0 ? (
        <details className="text-xs">
          <summary className="cursor-pointer text-muted-foreground">{rechte.length} Rechte</summary>
          <div className="mt-1 max-h-60 space-y-0.5 overflow-auto font-mono">
            {rechte.map(([key, geltung]) => (
              <div key={key} className="break-all">
                {key} <span className="text-muted-foreground">{geltung}</span>
              </div>
            ))}
          </div>
        </details>
      ) : null}
    </div>
  );
}

/**
 * Zeigt den erhobenen Kontext.
 *
 * Die beiden Puffer bekommen eine eigene Darstellung: als JSON-Klumpen in einer
 * Zeile wären genau die Angaben unlesbar, wegen derer der Kontext erhoben wird.
 */
function KontextListe({ kontext }: { kontext: Record<string, unknown> }) {
  const { console: konsole, requests, boundary, benutzer, ...rest } = (kontext ?? {}) as {
    console?: KonsolenZeile[];
    requests?: RequestZeile[];
    boundary?: { message?: string; stack?: string; componentStack?: string };
    benutzer?: BenutzerKontext;
  } & Record<string, unknown>;
  const eintraege = Object.entries(rest);
  if (eintraege.length === 0 && !konsole?.length && !requests?.length && !boundary && !benutzer) {
    return <p className="text-sm text-muted-foreground">Kein Kontext erfasst.</p>;
  }
  return (
    <div className="space-y-3">
      <dl className="space-y-1 text-xs">
        {eintraege.map(([schluessel, wert]) => (
          <div key={schluessel} className="flex gap-2">
            <dt className="w-32 shrink-0 text-muted-foreground">{schluessel}</dt>
            <dd className="break-all font-mono">
              {typeof wert === 'object' ? JSON.stringify(wert) : String(wert)}
            </dd>
          </div>
        ))}
      </dl>

      {benutzer ? <BenutzerAngaben benutzer={benutzer} /> : null}

      {boundary?.message && (
        <div className="space-y-1">
          <div className="text-xs font-medium text-muted-foreground">Absturz</div>
          <p className="break-all font-mono text-xs">{boundary.message}</p>
          {boundary.stack && (
            <pre className="max-h-40 overflow-auto rounded bg-muted p-2 text-[11px] leading-relaxed">
              {boundary.stack}
            </pre>
          )}
        </div>
      )}

      {requests && requests.length > 0 && (
        <div className="space-y-1">
          <div className="text-xs font-medium text-muted-foreground">
            Fehlgeschlagene Aufrufe
          </div>
          {requests.map((eintrag, index) => (
            <div key={index} className="break-all font-mono text-xs">
              {eintrag.method} {eintrag.path} → {eintrag.status}
              {eintrag.requestId ? ` (${eintrag.requestId})` : ''}
            </div>
          ))}
        </div>
      )}

      {konsole && konsole.length > 0 && (
        <div className="space-y-1">
          <div className="text-xs font-medium text-muted-foreground">Konsole</div>
          {konsole.map((eintrag, index) => (
            <div key={index} className="break-all font-mono text-xs">
              {eintrag.message}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Ticket im Detail: Rohtext, erhobener Kontext, Verlauf und Triage.
 *
 * Der Rohtext des Melders steht unverändert da — eine spätere KI-Aufbereitung
 * (Phase 3) tritt daneben, nicht darüber.
 */
export default function TicketDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const darfBearbeiten = usePermission(P.testing.tickets.tickets_bearbeiten);

  const [ticket, setTicket] = useState<TicketDetailDto | null>(null);
  const [anhaenge, setAnhaenge] = useState<TicketAttachmentDto[]>([]);
  const [zustaendige, setZustaendige] = useState<TicketAssigneeDto[]>([]);
  const [laedt, setLaedt] = useState(true);
  // Screenshot groß über der Seite statt im neuen Tab (T-0148).
  const [lightbox, setLightbox] = useState<string | null>(null);
  const [speichert, setSpeichert] = useState(false);
  // T-0224: Beim Umstellen von Hand den Melder per Mail bitten — Standard an.
  const [melderBitten, setMelderBitten] = useState(true);
  const [kommentar, setKommentar] = useState('');
  // Bilder zum Kommentar: erst im Browser gesammelt, hochgeladen wird beim
  // Abschicken — sonst lägen Dateien im Speicher, die zu nichts gehören.
  const [bilder, setBilder] = useState<Array<{ datei: File; vorschau: string }>>([]);
  const bildWahl = useRef<HTMLInputElement | null>(null);

  // Die Sammelkiste zeigt nur, was zur Meldung gehört — was an einem Kommentar
  // hängt, steht dort und soll nicht zweimal erscheinen.
  const anhaengeZurMeldung = useMemo(
    () => anhaenge.filter((bild) => !bild.historyEntryId),
    [anhaenge]
  );
  const [bearbeitet, setBearbeitet] = useState(false);
  const [entwurfTitel, setEntwurfTitel] = useState('');
  const [entwurfText, setEntwurfText] = useState('');
  const [zeigeOriginal, setZeigeOriginal] = useState(false);
  // Ticketnummern aus allen Texten der Seite, einmal aufgelöst. Ein Tooltip, der
  // erst beim Überfahren nachlädt, ist bei einem Verweis mitten im Satz nutzlos:
  // Bis er steht, ist die Maus weiter.
  const [verweise, setVerweise] = useState<Map<number, TicketVerweis>>(new Map());

  const laden = useCallback(async () => {
    if (!id) return;
    setLaedt(true);
    try {
      setTicket(await testingApi.get(Number(id)));
      // Die Leseadressen laufen ab; ein Fehler dabei darf das Ticket nicht verstecken.
      const bilder = await testingApi.attachments(Number(id)).catch(() => null);
      setAnhaenge(bilder?.items ?? []);
    } catch (fehler) {
      toast.error(
        fehler instanceof Error ? fehler.message : 'Das Ticket konnte nicht geladen werden.'
      );
      setTicket(null);
    } finally {
      setLaedt(false);
    }
  }, [id]);

  useEffect(() => {
    void laden();
  }, [laden]);

  /** Das Präfix des eigenen Nummernkreises steht im Label des Tickets selbst. */
  const praefix = ticket?.label?.[0] ?? 'T';

  // Beschreibung, gemeldeter Wortlaut und jeder Verlaufseintrag können Nummern
  // nennen — gesammelt wird über alle, geholt wird einmal.
  useEffect(() => {
    if (!ticket) return;
    const texte = [
      ticket.body,
      ticket.reportedBody ?? '',
      ...(ticket.history ?? []).flatMap((eintrag) => [
        eintrag.description ?? '',
        eintrag.details ?? '',
      ]),
    ];
    const nummern = [
      ...new Set(texte.flatMap((text) => nummernImText(text, praefix))),
    ].filter((nummer) => nummer !== ticket.number);
    if (nummern.length === 0) {
      setVerweise(new Map());
      return;
    }
    // Ein Fehler hier darf das Ticket nicht verstecken: Dann bleiben die Nummern
    // gewöhnlicher Text, und alles andere funktioniert weiter.
    testingApi
      .refs(nummern)
      .then((daten) =>
        setVerweise(new Map((daten?.items ?? []).map((eintrag) => [eintrag.number, eintrag])))
      )
      .catch(() => setVerweise(new Map()));
  }, [ticket, praefix]);

  // Wer zuweisbar ist, verlangt „tickets_bearbeiten". Ohne das Recht bleibt die
  // Liste leer und die Auswahl verschwindet — statt einer Fehlermeldung.
  useEffect(() => {
    if (!darfBearbeiten) return;
    testingApi
      .assignees()
      .then((daten) => setZustaendige(daten?.items ?? []))
      .catch(() => setZustaendige([]));
  }, [darfBearbeiten]);

  /**
   * Zusammenführen ist immer eine Entscheidung, nie ein Automatismus: Der Dienst
   * schlägt vor, hier bestätigt jemand.
   */
  const alsDubletteFuehren = async (zielId: number) => {
    if (!ticket) return;
    setSpeichert(true);
    try {
      await testingApi.patch(ticket.id, { duplicateOfId: zielId, status: 'duplikat' });
      await laden();
      toast.success('Als Dublette geführt.');
    } catch (fehler) {
      toast.error(
        fehler instanceof Error ? fehler.message : 'Die Zuordnung wurde nicht übernommen.'
      );
    } finally {
      setSpeichert(false);
    }
  };

  const zuweisen = async (accountId: string | null) => {
    if (!ticket) return;
    setSpeichert(true);
    try {
      await testingApi.patch(ticket.id, {
        assignedAccountId: accountId,
        // Damit die Mail einen Link auf dieses Office trägt.
        officeBaseUrl: window.location.origin,
      });
      await laden();
      toast.success(accountId ? 'Zugewiesen — die Person bekommt eine Mail.' : 'Zuständigkeit entzogen.');
    } catch (fehler) {
      toast.error(
        fehler instanceof Error ? fehler.message : 'Die Zuweisung wurde nicht übernommen.'
      );
    } finally {
      setSpeichert(false);
    }
  };

  /** Wechselt in den Bearbeitungsmodus und füllt die Felder mit dem Ist-Stand. */
  const bearbeiten = () => {
    if (!ticket) return;
    setEntwurfTitel(ticket.title);
    setEntwurfText(ticket.body);
    setBearbeitet(true);
  };

  const uebernehmen = async () => {
    if (!ticket) return;
    const titel = entwurfTitel.trim();
    const text = entwurfText.trim();
    if (!titel || !text) {
      toast.error('Titel und Beschreibung dürfen nicht leer sein.');
      return;
    }
    setSpeichert(true);
    try {
      await testingApi.patch(ticket.id, {
        title: titel,
        body: text,
        // Wer in der Beschreibung steht, bekommt eine Mail. Abgeleitet aus dem
        // Text: Wer einen Namen wieder herauslöscht, meint ihn auch nicht mehr.
        mentions: erwaehnteKonten(text, zustaendige),
        officeBaseUrl: window.location.origin,
      });
      setBearbeitet(false);
      // Neu laden statt die Patch-Antwort zu übernehmen: Sie trägt keinen
      // Verlauf, und die Änderung hat gerade einen Eintrag erzeugt.
      await laden();
      toast.success('Gespeichert.');
    } catch (fehler) {
      toast.error(
        fehler instanceof Error ? fehler.message : 'Die Änderung wurde nicht übernommen.'
      );
    } finally {
      setSpeichert(false);
    }
  };

  const aendern = async (felder: {
    status?: TicketStatus;
    severity?: TicketSeverity;
    kind?: TicketKind;
  }) => {
    if (!ticket) return;
    setSpeichert(true);
    try {
      // Die Antwort des Patch trägt keinen Verlauf; sie hier in den Zustand zu
      // schreiben, hat die Seite zum Absturz gebracht (T-0036, T-0038). Neu laden
      // ist ohnehin nötig, weil der Statuswechsel einen Eintrag erzeugt.
      await testingApi.patch(
        ticket.id,
        felder.status === 'auf_testsystem'
          ? { ...felder, melderBitten, officeBaseUrl: window.location.origin }
          : felder,
      );
      await laden();
      toast.success('Gespeichert.');
    } catch (fehler) {
      toast.error(
        fehler instanceof Error ? fehler.message : 'Die Änderung wurde nicht übernommen.'
      );
    } finally {
      setSpeichert(false);
    }
  };

  /** Ein Bild aus der Zwischenablage oder der Dateiauswahl übernehmen. */
  const uebernehmeBild = (datei: File) => {
    if (!SCREENSHOT_TYPES.includes(datei.type)) {
      toast.error('Als Screenshot gehen PNG, JPEG und WebP.');
      return;
    }
    if (datei.size > SCREENSHOT_MAX_BYTES) {
      toast.error('Der Screenshot ist zu groß (mehr als 8 MB).');
      return;
    }
    setBilder((vorher) => [...vorher, { datei, vorschau: URL.createObjectURL(datei) }]);
  };

  const entferneBild = (vorschau: string) => {
    URL.revokeObjectURL(vorschau);
    setBilder((vorher) => vorher.filter((bild) => bild.vorschau !== vorschau));
  };

  const kommentieren = async () => {
    if (!ticket || !kommentar.trim()) return;
    setSpeichert(true);
    try {
      // Erst die Bilder, dann der Kommentar: Scheitert ein Upload, steht noch
      // nichts im Verlauf, und der Text ist nicht verloren.
      const kennungen: number[] = [];
      for (const bild of bilder) {
        const angelegt = await testingApi.uploadScreenshot(ticket.id, bild.datei);
        kennungen.push(angelegt.id);
      }
      await testingApi.comment(
        ticket.id,
        kommentar.trim(),
        kennungen,
        erwaehnteKonten(kommentar, zustaendige)
      );
      bilder.forEach((bild) => URL.revokeObjectURL(bild.vorschau));
      setBilder([]);
      setKommentar('');
      await laden();
    } catch (fehler) {
      toast.error(
        fehler instanceof Error ? fehler.message : 'Der Kommentar wurde nicht gespeichert.'
      );
    } finally {
      setSpeichert(false);
    }
  };

  if (laedt) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // Sicherheitshalber entpacken: eine unvollständige Antwort darf die Ansicht
  // höchstens leer lassen, nie zum Absturz bringen.
  const verlauf = Array.isArray(ticket?.history) ? ticket.history : [];
  const dubletten = Array.isArray(ticket?.duplicateCandidates) ? ticket.duplicateCandidates : [];
  // Wer ich bin, sagt die Zuständigen-Liste — der Browser rät seine Konto-ID nicht.
  const ich = zustaendige.find((person) => person.isMe);
  const gehoertMir = !!ich && ticket?.assignedAccountId === ich.accountId;
  // Nur Status anbieten, die der Server auch annimmt — sonst endet jede zweite
  // Auswahl in einer Fehlermeldung.
  const moeglicheStatus = Array.isArray(ticket?.allowedStatus) && ticket.allowedStatus.length > 0
    ? ticket.allowedStatus
    : (Object.keys(TICKET_STATUS_LABEL) as TicketStatus[]);
  // Abgeschlossen heisst festgeschrieben: Der Server weist jede Änderung ab,
  // bis jemand das Ticket wieder öffnet. Die Oberfläche macht das sichtbar,
  // statt den Bediener in die Fehlermeldung laufen zu lassen.
  const gesperrt = ticket?.closed === true;
  const darfAendern = darfBearbeiten && !gesperrt;

  if (!ticket) {
    return (
      <div className="mx-auto max-w-2xl space-y-4">
        <div className="rounded-lg border border-dashed p-6 text-sm text-muted-foreground">
          Dieses Ticket gibt es nicht (mehr).
        </div>
        <Button variant="outline" onClick={() => navigate('/testing')}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Zur Liste
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="space-y-1">
          <Button variant="ghost" size="sm" className="-ml-2" onClick={() => navigate('/testing')}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Tickets
          </Button>
          {bearbeitet ? (
            <div className="flex items-center gap-2">
              <span className="font-mono text-base text-muted-foreground">{ticket.label}</span>
              <Input
                value={entwurfTitel}
                onChange={(event) => setEntwurfTitel(event.target.value)}
                maxLength={200}
                className="h-9 w-[28rem] max-w-full text-lg font-semibold"
                aria-label="Titel"
              />
            </div>
          ) : (
            <h1 className="text-2xl font-semibold leading-7">
              <span className="mr-2 font-mono text-base text-muted-foreground">{ticket.label}</span>
              {ticket.title}
            </h1>
          )}
          <p className="text-sm text-muted-foreground">
            {TICKET_KIND_LABEL[ticket.kind]} · gemeldet von {ticket.reporterName ?? 'unbekannt'}
            {ticket.reporterRole ? ` (${ticket.reporterRole})` : ''} · {zeit(ticket.createdAt)}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant={ticket.severity === 'blocker' ? 'destructive' : 'secondary'}>
            {TICKET_SEVERITY_LABEL[ticket.severity]}
          </Badge>
          <Badge variant="outline">{TICKET_STATUS_LABEL[ticket.status]}</Badge>
          {ticket.assignedName && <Badge variant="secondary">{ticket.assignedName}</Badge>}
          {darfBearbeiten && ticket.status === 'nachtest_ok' && (
            <Button
              size="sm"
              className="gap-1.5"
              disabled={speichert}
              onClick={() => void aendern({ status: 'erledigt' })}
            >
              <CheckCircle2 className="h-3.5 w-3.5" />
              Schließen
            </Button>
          )}
          {darfBearbeiten && gesperrt && (
            <Button
              size="sm"
              variant="outline"
              className="gap-1.5"
              disabled={speichert}
              onClick={() => void aendern({ status: 'triagiert' })}
            >
              <RotateCcw className="h-3.5 w-3.5" />
              Wieder öffnen
            </Button>
          )}
          {darfAendern && !bearbeitet && (
            <Button variant="outline" size="sm" className="gap-1.5" onClick={bearbeiten}>
              <Pencil className="h-3.5 w-3.5" />
              Bearbeiten
            </Button>
          )}
        </div>
      </div>

      {ticket.status === 'auf_testsystem' && (
        <Card className="border-primary/40 bg-primary/5">
          <CardHeader>
            <CardTitle className="text-base">Bitte nachtesten</CardTitle>
            <CardDescription>
              Der Fix ist ausgerollt
              {ticket.fixedVersion ? ` (Version ${ticket.fixedVersion})` : ''}. Stimmt es jetzt?
            </CardDescription>
          </CardHeader>
          <CardContent className="flex flex-wrap gap-2">
            <Button
              size="sm"
              disabled={speichert || !darfBearbeiten}
              onClick={() => void aendern({ status: 'nachtest_ok' })}
            >
              Nachtest in Ordnung
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={speichert || !darfBearbeiten}
              onClick={() => void aendern({ status: 'in_arbeit' })}
            >
              Stimmt noch nicht
            </Button>
          </CardContent>
        </Card>
      )}

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Meldung</CardTitle>
              <CardDescription>
                {ticket.bodyEdited
                  ? 'Nachbearbeitet — der gemeldete Wortlaut steht darunter.'
                  : 'Wortlaut des Melders, unverändert.'}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {bearbeitet ? (
                <>
                  <MarkdownEditor
                    value={entwurfText}
                    onChange={setEntwurfText}
                    personen={zustaendige}
                    verweise={verweise}
                    praefix={praefix}
                    ohneTicket={ticket.number}
                    rows={12}
                    aria-label="Beschreibung"
                  />
                  <p className="text-xs text-muted-foreground">
                    Die ursprüngliche Meldung bleibt erhalten; sie wird nicht überschrieben.
                    Markdown geht (**fett**, Listen, Links). Mit{' '}
                    <span className="font-mono">@</span> jemanden erwähnen — die Person
                    bekommt eine Mail. Eine Ticketnummer wie{' '}
                    <span className="font-mono">{praefix}-0001</span> wird zum Link.
                  </p>
                  <div className="flex gap-2">
                    <Button size="sm" disabled={speichert} onClick={() => void uebernehmen()}>
                      {speichert && <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />}
                      Speichern
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      disabled={speichert}
                      onClick={() => setBearbeitet(false)}
                    >
                      Abbrechen
                    </Button>
                  </div>
                </>
              ) : (
                <>
                  <MarkdownText
                    text={ticket.body}
                    personen={zustaendige}
                    verweise={verweise}
                    praefix={praefix}
                    ohneTicket={ticket.number}
                    className="text-sm"
                  />
                  {ticket.bodyEdited && (
                    <div className="space-y-2 border-t pt-3">
                      <Button
                        variant="link"
                        size="sm"
                        className="h-auto p-0 text-xs"
                        onClick={() => setZeigeOriginal((vorher) => !vorher)}
                      >
                        {zeigeOriginal ? 'Ursprüngliche Meldung ausblenden' : 'Ursprüngliche Meldung anzeigen'}
                      </Button>
                      {zeigeOriginal && (
                        <p className="whitespace-pre-wrap text-sm text-muted-foreground">
                          {ticket.reportedBody}
                        </p>
                      )}
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>

          {(ticket.aiSummary || ticket.aiSteps) && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Aufbereitung</CardTitle>
                <CardDescription>Maschinell erzeugt — der Wortlaut oben zählt.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                {ticket.aiSummary && <p className="whitespace-pre-wrap">{ticket.aiSummary}</p>}
                {ticket.aiSteps && (
                  <p className="whitespace-pre-wrap text-muted-foreground">{ticket.aiSteps}</p>
                )}
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Verlauf</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {verlauf.length === 0 && (
                <p className="text-sm text-muted-foreground">Noch nichts passiert.</p>
              )}
              {verlauf.map((eintrag) => (
                <div key={eintrag.id} className="border-l-2 border-border pl-3">
                  {/* Kommentare sind Markdown (T-0160), Statuswechsel bleiben Klartext. */}
                  {eintrag.type === 'ticket_kommentar' ? (
                    <MarkdownText
                      text={eintrag.description ?? ''}
                      personen={zustaendige}
                      verweise={verweise}
                      praefix={praefix}
                      ohneTicket={ticket.number}
                      className="text-sm"
                    />
                  ) : (
                    <MentionText
                      text={eintrag.description ?? ''}
                      personen={zustaendige}
                      verweise={verweise}
                      praefix={praefix}
                      ohneTicket={ticket.number}
                      className="text-sm"
                    />
                  )}
                  {eintrag.details && (
                    <MentionText
                      text={eintrag.details}
                      personen={zustaendige}
                      verweise={verweise}
                      praefix={praefix}
                      ohneTicket={ticket.number}
                      className="text-sm text-muted-foreground"
                    />
                  )}
                  {(eintrag.attachments ?? []).length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-2">
                      {(eintrag.attachments ?? []).map((bild) =>
                        bild.url ? (
                          <button
                            key={bild.id}
                            type="button"
                            onClick={() => setLightbox(bild.url ?? null)}
                            className="cursor-zoom-in"
                          >
                            <img
                              src={bild.url}
                              alt="Bild zum Kommentar"
                              className="h-24 rounded-md border object-cover"
                            />
                          </button>
                        ) : (
                          <span key={bild.id} className="text-xs text-muted-foreground">
                            Bild vorhanden, der Speicher antwortet gerade nicht.
                          </span>
                        )
                      )}
                    </div>
                  )}
                  <div className="text-xs text-muted-foreground">
                    {eintrag.actorName ?? 'System'} · {zeit(eintrag.createdAt)}
                  </div>
                </div>
              ))}

              {darfBearbeiten && (
                <div className="space-y-2 pt-2">
                  <Label htmlFor="ticket-kommentar">Kommentar</Label>
                  <MarkdownEditor
                    id="ticket-kommentar"
                    value={kommentar}
                    onChange={setKommentar}
                    personen={zustaendige}
                    verweise={verweise}
                    praefix={praefix}
                    ohneTicket={ticket.number}
                    onPaste={(ereignis) => {
                      // Bildschirmfoto machen, Strg+V — derselbe Weg wie beim Melden.
                      const bild = Array.from(ereignis.clipboardData?.items ?? []).find(
                        (eintrag) => eintrag.type.startsWith('image/')
                      );
                      const datei = bild?.getAsFile();
                      if (datei) {
                        ereignis.preventDefault();
                        uebernehmeBild(datei);
                      }
                    }}
                    rows={3}
                    placeholder={`Was hast du herausgefunden? @ erwähnt jemanden, ${praefix}-0001 verlinkt ein Ticket, Strg+V fügt einen Screenshot ein.`}
                  />

                  {bilder.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {bilder.map((bild) => (
                        <div key={bild.vorschau} className="relative">
                          <img
                            src={bild.vorschau}
                            alt="Angehängtes Bild"
                            className="h-20 rounded-md border object-cover"
                          />
                          <button
                            type="button"
                            aria-label="Bild entfernen"
                            className="absolute -right-2 -top-2 rounded-full border bg-background p-0.5 shadow-sm"
                            onClick={() => entferneBild(bild.vorschau)}
                          >
                            <X className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      onClick={() => void kommentieren()}
                      disabled={speichert || !kommentar.trim()}
                    >
                      <MessageSquarePlus className="mr-2 h-4 w-4" />
                      Hinzufügen
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="gap-1.5"
                      disabled={speichert}
                      onClick={() => bildWahl.current?.click()}
                    >
                      <ImagePlus className="h-3.5 w-3.5" />
                      Bild anhängen
                    </Button>
                    <input
                      ref={bildWahl}
                      type="file"
                      accept={SCREENSHOT_TYPES.join(',')}
                      className="hidden"
                      onChange={(ereignis) => {
                        const datei = ereignis.target.files?.[0];
                        if (datei) uebernehmeBild(datei);
                        ereignis.target.value = '';
                      }}
                    />
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        <div className="space-y-6">
          {darfAendern && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Triage</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Zuständig</Label>
                  <Select
                    value={ticket.assignedAccountId ?? 'niemand'}
                    onValueChange={(wert) => void zuweisen(wert === 'niemand' ? null : wert)}
                    disabled={speichert || zustaendige.length === 0}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="niemand" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="niemand">niemand</SelectItem>
                      {zustaendige.map((person) => (
                        <SelectItem key={person.accountId} value={person.accountId}>
                          {person.name}
                          {person.isMe ? ' (ich)' : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {ticket.assignedName && ticket.assignedAt && (
                    <p className="text-xs text-muted-foreground">
                      seit {zeit(ticket.assignedAt)}
                    </p>
                  )}
                  {!gehoertMir && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full gap-1.5"
                      disabled={speichert}
                      onClick={() => void zuweisen('mir')}
                    >
                      <UserCheck className="h-3.5 w-3.5" />
                      Mir zuweisen
                    </Button>
                  )}
                </div>
                <div className="space-y-2">
                  <Label>Status</Label>
                  <Select
                    value={ticket.status}
                    onValueChange={(wert) => void aendern({ status: wert as TicketStatus })}
                    disabled={speichert}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {moeglicheStatus.map((wert) => (
                        <SelectItem key={wert} value={wert}>
                          {TICKET_STATUS_LABEL[wert]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {ticket.status !== 'auf_testsystem' && moeglicheStatus.includes('auf_testsystem') ? (
                    <label className="flex items-center gap-2 text-xs text-muted-foreground">
                      <input
                        type="checkbox"
                        className="h-3.5 w-3.5"
                        checked={melderBitten}
                        onChange={(e) => setMelderBitten(e.target.checked)}
                      />
                      Bei „{TICKET_STATUS_LABEL.auf_testsystem}“ den Melder per Mail um den Nachtest bitten
                    </label>
                  ) : null}
                </div>
                <div className="space-y-2">
                  <Label>Schweregrad</Label>
                  <Select
                    value={ticket.severity}
                    onValueChange={(wert) => void aendern({ severity: wert as TicketSeverity })}
                    disabled={speichert}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {(Object.keys(TICKET_SEVERITY_LABEL) as TicketSeverity[]).map((wert) => (
                        <SelectItem key={wert} value={wert}>
                          {TICKET_SEVERITY_LABEL[wert]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Art</Label>
                  <Select
                    value={ticket.kind}
                    onValueChange={(wert) => void aendern({ kind: wert as TicketKind })}
                    disabled={speichert}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {(Object.keys(TICKET_KIND_LABEL) as TicketKind[]).map((wert) => (
                        <SelectItem key={wert} value={wert}>
                          {TICKET_KIND_LABEL[wert]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <StoryFeld
                  ticketId={ticket.id}
                  storyKey={ticket.storyKey ?? null}
                  disabled={speichert}
                  onGespeichert={laden}
                />
                {ticket.duplicateOfId && (
                  <p className="text-sm text-muted-foreground">
                    Dublette von Ticket #{ticket.duplicateOfId}
                  </p>
                )}
              </CardContent>
            </Card>
          )}

          {dubletten.length > 0 && ticket.status !== 'duplikat' && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Könnte dasselbe sein</CardTitle>
                <CardDescription>
                  Vorschlag aus dem Textvergleich — entschieden wird hier.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {dubletten.map((kandidat) => (
                  <div key={kandidat.id} className="space-y-1 border-l-2 border-border pl-3">
                    <button
                      type="button"
                      className="text-left text-sm font-medium hover:underline"
                      onClick={() => navigate(`/testing/${kandidat.id}`)}
                    >
                      <span className="mr-2 font-mono text-xs text-muted-foreground">
                        {kandidat.label}
                      </span>
                      {kandidat.title}
                    </button>
                    <div className="text-xs text-muted-foreground">
                      {kandidat.reason} · {TICKET_STATUS_LABEL[kandidat.status]}
                    </div>
                    {darfAendern && (
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={speichert}
                        onClick={() => void alsDubletteFuehren(kandidat.id)}
                        className="mt-1 gap-1.5"
                      >
                        <Copy className="h-3.5 w-3.5" />
                        Ist dasselbe wie {kandidat.label}
                      </Button>
                    )}
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          <BildLightbox url={lightbox} titel="Screenshot" onSchliessen={() => setLightbox(null)} />
          {anhaengeZurMeldung.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Screenshots</CardTitle>
                <CardDescription>
                  Wird
                  {anhaengeZurMeldung[0]?.deleteAfter
                    ? ` am ${new Date(anhaengeZurMeldung[0].deleteAfter).toLocaleDateString('de-DE')}`
                    : ' nach Ablauf der Frist'}{' '}
                  automatisch gelöscht.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-2">
                {anhaengeZurMeldung.map((bild) =>
                  bild.url ? (
                    <button
                      key={bild.id}
                      type="button"
                      onClick={() => setLightbox(bild.url ?? null)}
                      className="block w-full cursor-zoom-in"
                    >
                      <img
                        src={bild.url}
                        alt="Screenshot zur Meldung"
                        className="w-full rounded-md border"
                      />
                    </button>
                  ) : (
                    <p key={bild.id} className="text-sm text-muted-foreground">
                      Screenshot vorhanden, der Speicher antwortet gerade nicht.
                    </p>
                  )
                )}
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Kontext</CardTitle>
              <CardDescription>Zustand zum Meldezeitpunkt.</CardDescription>
            </CardHeader>
            <CardContent>
              <KontextListe kontext={ticket.context} />
            </CardContent>
          </Card>

          {(ticket.fixedCommit || ticket.fixedVersion) && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Behoben in</CardTitle>
              </CardHeader>
              <CardContent className="space-y-1 text-sm">
                {ticket.fixedVersion && <div>Version {ticket.fixedVersion}</div>}
                {ticket.fixedCommit && (
                  <div className="font-mono text-xs">{ticket.fixedCommit}</div>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Story aus dem Abnahme-Katalog, zu der das Ticket gehört („PERS-3").
 *
 * Ein Feld mit Vorschlägen statt einer starren Auswahl: Auf dem Testsystem
 * kommen die Vorschläge aus dem Spiegel des Live-Katalogs, der alle 15 Minuten
 * nachgezogen wird — eine gerade angelegte Story lässt sich trotzdem eintippen.
 */
function StoryFeld({
  ticketId,
  storyKey,
  disabled,
  onGespeichert,
}: {
  ticketId: number;
  storyKey: string | null;
  disabled: boolean;
  onGespeichert: () => Promise<void> | void;
}) {
  const [wert, setWert] = useState(storyKey ?? '');
  const [speichert, setSpeichert] = useState(false);
  const [auswahl, setAuswahl] = useState<StoryAuswahlDto['items']>([]);
  useEffect(() => setWert(storyKey ?? ''), [storyKey]);
  useEffect(() => {
    abnahmeApi
      .storyAuswahl()
      .then((daten) => setAuswahl(daten?.items ?? []))
      .catch(() => setAuswahl([]));
  }, []);

  const sauber = wert.trim().toUpperCase();
  const gueltig = sauber === '' || /^[A-Z]{2,8}-\d{1,4}$/.test(sauber);
  const geaendert = sauber !== (storyKey ?? '');
  const treffer = auswahl.find((eintrag) => eintrag.key === sauber);

  const speichern = async () => {
    setSpeichert(true);
    try {
      await testingApi.patch(ticketId, { storyKey: sauber || null });
      await onGespeichert();
      toast.success(sauber ? `Mit ${sauber} verbunden.` : 'Story-Verbindung gelöst.');
    } catch (fehler) {
      toast.error(fehler instanceof Error ? fehler.message : 'Die Story wurde nicht übernommen.');
    } finally {
      setSpeichert(false);
    }
  };

  return (
    <div className="space-y-2">
      <Label htmlFor="ticket-story">Story</Label>
      <div className="flex gap-2">
        <Input
          id="ticket-story"
          value={wert}
          placeholder="z. B. PERS-3"
          className="font-mono"
          list="ticket-story-liste"
          onChange={(event) => setWert(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && gueltig && geaendert) void speichern();
          }}
        />
        <Button
          variant="outline"
          disabled={disabled || speichert || !gueltig || !geaendert}
          onClick={() => void speichern()}
        >
          Setzen
        </Button>
      </div>
      <datalist id="ticket-story-liste">
        {auswahl.map((eintrag) => (
          <option key={eintrag.key} value={eintrag.key}>
            {eintrag.epicKey} · {eintrag.title}
          </option>
        ))}
      </datalist>
      {treffer && <p className="text-xs text-muted-foreground">{treffer.title}</p>}
      {gueltig && sauber && !treffer && auswahl.length > 0 && (
        <p className="text-xs text-amber-700 dark:text-amber-300">Diese Story steht (noch) nicht im Katalog.</p>
      )}
      {!gueltig && <p className="text-xs text-destructive">Form: Präfix, Bindestrich, Nummer — PERS-3.</p>}
    </div>
  );
}
